import type { CampaignCreationCapabilities, SmartCreationReviewWorkspace } from "@adpilot/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import type { ReviewChangeHandler } from "./review-change-handler";

export function matchingBudgetSuggestion(workspace: SmartCreationReviewWorkspace) {
  const suggestion = workspace.budgetSuggestion;
  const included = workspace.graph.campaigns.filter((campaign) => !campaign.excluded.effective);
  if (
    (workspace.status !== "ready" && workspace.status !== "confirmed") ||
    suggestion?.status !== "suggested" ||
    suggestion.allocations.length !== included.length
  )
    return null;
  const amounts = new Map(
    suggestion.allocations.map((allocation) => [allocation.campaignKey, allocation.dailyBudget]),
  );
  if (!included.every((campaign) => campaign.ref.itemKey && amounts.has(campaign.ref.itemKey)))
    return null;
  const total = [...amounts.values()].reduce((sum, amount) => sum + amount, 0);
  if (
    Math.abs(total - included.reduce((sum, campaign) => sum + campaign.dailyBudget.effective, 0)) >
    0.000001
  )
    return null;
  return included.map((campaign) => ({
    ref: campaign.ref,
    allocation: { basis: "amount" as const, amount: amounts.get(campaign.ref.itemKey!)! },
  }));
}

export function hasManualBudgetEdits(workspace: SmartCreationReviewWorkspace) {
  const suggested = new Map(
    workspace.suggestedDraft.campaigns.map((campaign) => [campaign.itemKey, campaign.dailyBudget]),
  );
  return workspace.graph.campaigns.some(
    (campaign) =>
      campaign.excluded.effective ||
      suggested.get(campaign.ref.itemKey ?? "") !== campaign.dailyBudget.effective,
  );
}

export function currentBudgetSuggestion(workspace: SmartCreationReviewWorkspace) {
  return workspace.status === "ready" ? matchingBudgetSuggestion(workspace) : null;
}

interface ReviewBudgetDialogProps {
  open?: boolean;
  workspace: SmartCreationReviewWorkspace;
  capabilities?: CampaignCreationCapabilities;
  disabled: boolean;
  onApply: ReviewChangeHandler;
  onClose(): void;
  onDirtyChange(dirty: boolean): void;
}

export function ReviewBudgetDialog({
  open = true,
  workspace,
  capabilities,
  disabled,
  onApply,
  onClose,
  onDirtyChange,
}: ReviewBudgetDialogProps) {
  const { t, i18n } = useTranslation("smartCreation");
  const campaigns = workspace.graph.campaigns.filter((campaign) => !campaign.excluded.effective);
  // Untouched fields follow canonical refetches; only user edits create a local draft.
  const [stagedAmounts, setAmounts] = useState<Record<string, string> | null>(null);
  const amounts =
    stagedAmounts ??
    Object.fromEntries(
      campaigns.map((campaign) => [
        campaign.ref.lineageKey,
        String(campaign.dailyBudget.effective),
      ]),
    );
  const [invalid, setInvalid] = useState(false);
  const dirty = campaigns.some(
    (campaign) => amounts[campaign.ref.lineageKey] !== String(campaign.dailyBudget.effective),
  );
  useEffect(() => {
    onDirtyChange(open && dirty);
  }, [open, dirty, onDirtyChange]);
  useEffect(() => {
    if (!open) {
      setAmounts(null);
      setInvalid(false);
    }
  }, [open]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  const currency = new Intl.NumberFormat(i18n.resolvedLanguage, {
    style: "currency",
    currency: workspace.summary.currencyCode,
  });
  const limits = capabilities?.dailyBudget;
  const scale = 10 ** (limits?.decimals ?? currency.resolvedOptions().maximumFractionDigits ?? 2);
  const poolUnits = Math.round(workspace.summary.totalDailyBudget * scale);
  // Each Campaign keeps at least the marketplace minimum, not merely one currency unit.
  const minUnits = Math.max(1, Math.ceil((limits?.min ?? 0) * scale - 1e-9));
  const maxUnits = limits ? Math.floor(limits.max * scale + 1e-9) : Number.POSITIVE_INFINITY;
  const sliderMaxUnits = poolUnits - (campaigns.length - 1) * minUnits;
  const redistributable = campaigns.length >= 2 && poolUnits >= campaigns.length * minUnits;
  function redistribute(key: string, requested: number) {
    if (!redistributable) return;
    const chosen = Math.min(sliderMaxUnits, Math.max(minUnits, Math.round(requested)));
    const others = campaigns.filter((campaign) => campaign.ref.lineageKey !== key);
    // Reserve the minimum per activity, then distribute the remainder
    // proportionally using integer units so rounding cannot change the pool.
    const remainder = poolUnits - chosen - others.length * minUnits;
    const weights = others.map((campaign) =>
      Math.max(0, Math.round(Number(amounts[campaign.ref.lineageKey]) * scale) - minUnits),
    );
    const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
    const shares = others.map((campaign, index) => {
      const raw =
        remainder * (weightTotal > 0 ? (weights[index] ?? 0) / weightTotal : 1 / others.length);
      return {
        key: campaign.ref.lineageKey,
        units: minUnits + Math.floor(raw),
        fraction: raw - Math.floor(raw),
      };
    });
    let rounding = poolUnits - chosen - shares.reduce((sum, share) => sum + share.units, 0);
    for (const share of [...shares].sort((a, b) => b.fraction - a.fraction)) {
      if (rounding > 0) {
        share.units += 1;
        rounding -= 1;
      }
    }
    setAmounts(
      Object.fromEntries([
        [key, String(chosen / scale)],
        ...shares.map((share) => [share.key, String(share.units / scale)]),
      ]),
    );
    setInvalid(false);
  }
  const total = campaigns.reduce(
    (sum, campaign) => sum + Number(amounts[campaign.ref.lineageKey] ?? ""),
    0,
  );
  const withinLimits = campaigns.every((campaign) => {
    const amount = Number(amounts[campaign.ref.lineageKey]);
    const units = amount * scale;
    return (
      amount > 0 &&
      Math.abs(units - Math.round(units)) < 1e-6 &&
      Math.round(units) >= minUnits &&
      Math.round(units) <= maxUnits
    );
  });
  const totalMatches =
    Number.isFinite(total) && Math.abs(total - workspace.summary.totalDailyBudget) < 0.000001;
  const valid = totalMatches && withinLimits;
  const suggestion = currentBudgetSuggestion(workspace);
  const matching = matchingBudgetSuggestion(workspace);
  const usesSuggestion =
    matching !== null
      ? matching.every((item) => Number(amounts[item.ref.lineageKey]) === item.allocation.amount)
      : !dirty && !hasManualBudgetEdits(workspace);
  const readOnly = disabled || workspace.status === "confirmed";
  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open && !disabled && !dirty) onClose();
      }}
    >
      <DialogPopup closeProps={{ disabled: disabled || dirty }}>
        <DialogHeader>
          <DialogTitle>{t("review.preview.budgetTitle")}</DialogTitle>
          <DialogDescription>{t("review.preview.budgetDescription")}</DialogDescription>
        </DialogHeader>
        <DialogPanel className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("preview.totalBudget")}</span>
            <strong className="text-lg tabular-nums">
              {currency.format(workspace.summary.totalDailyBudget)}
            </strong>
          </div>
          {campaigns.map((campaign) => (
            <div key={campaign.ref.lineageKey} className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-4">
                <span className="min-w-0 truncate text-sm" title={campaign.name.effective}>
                  {campaign.name.effective}
                </span>
                <span className="shrink-0 text-sm font-medium tabular-nums">
                  {currency.format(Number(amounts[campaign.ref.lineageKey]))}
                </span>
              </div>
              <Slider
                aria-label={campaign.name.effective}
                min={minUnits}
                max={Math.max(minUnits, sliderMaxUnits)}
                step={1}
                value={Math.round(Number(amounts[campaign.ref.lineageKey]) * scale)}
                disabled={readOnly || !redistributable}
                onValueChange={(value) =>
                  redistribute(
                    campaign.ref.lineageKey,
                    Array.isArray(value) ? (value[0] ?? 1) : value,
                  )
                }
              />
            </div>
          ))}
          {invalid || (dirty && !valid) ? (
            <p role="alert" className="text-xs text-destructive">
              {totalMatches && !withinLimits && limits
                ? t("review.preview.budgetCampaignRange", {
                    min: currency.format(limits.min),
                    max: currency.format(limits.max),
                    decimals: limits.decimals,
                  })
                : t("review.preview.budgetTotalError", {
                    total: currency.format(workspace.summary.totalDailyBudget),
                  })}
            </p>
          ) : null}
          {usesSuggestion && workspace.budgetSuggestion?.status === "suggested" ? (
            <p className="text-[11px] leading-[18px] text-muted-foreground">
              {t("review.preview.suggestionHistory", {
                start: workspace.budgetSuggestion.windowStart,
                end: workspace.budgetSuggestion.windowEnd,
              })}
            </p>
          ) : (
            <p className="text-[11px] leading-[18px] text-muted-foreground">
              {t(
                usesSuggestion
                  ? "review.preview.allocationFallback"
                  : "review.preview.allocationManualHint",
              )}
            </p>
          )}
          {suggestion ? (
            <Button
              size="sm"
              variant="outline"
              disabled={readOnly}
              onClick={() =>
                setAmounts(
                  Object.fromEntries(
                    suggestion.map((item) => [item.ref.lineageKey, String(item.allocation.amount)]),
                  ),
                )
              }
            >
              {t("review.summary.budgetSuggestion.apply")}
            </Button>
          ) : null}
        </DialogPanel>
        <DialogFooter>
          <Button variant="outline" size="sm" disabled={disabled} onClick={onClose}>
            {t("review.parity.cancel")}
          </Button>
          {workspace.status !== "confirmed" ? (
            <Button
              size="sm"
              disabled={readOnly || !dirty}
              onClick={async () => {
                if (!valid) {
                  setInvalid(true);
                  return;
                }
                const result = await onApply([
                  {
                    operation: "set_campaign_budget_allocations",
                    allocations: campaigns.map((campaign) => ({
                      ref: campaign.ref,
                      allocation: {
                        basis: "amount",
                        amount: Number(amounts[campaign.ref.lineageKey]),
                      },
                    })),
                  },
                ]);
                if (result !== false) onClose();
              }}
            >
              {t("review.preview.saveAllocation")}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
